/**
 * Hjälpfunktioner för fordonskortet "Allmän information" — data från Car.info extended_attributes + history.
 * Attribut tolkas via id (se carInfoSpecIds.ts), aldrig via attributnamn.
 */

export {
  EXT_IN_TRAFFIC,
  EXT_NEXT_INSPECTION,
  SPEC_ID_TAXATION_MONTHS,
  SPEC_ID_VEHICLE_MALUS,
  SPEC_ID_VEHICLE_TAX,
} from './carInfoSpecIds';

export interface CarInfoExtendedAttr {
  id?: string;
  name?: string;
  unit?: string;
  values?: unknown[];
  attributes?: Record<string, { name?: string; values?: unknown[] }>;
}

export interface CarInfoHistoryEvent {
  type?: string;
  date?: string;
  km?: number;
  city?: string;
  company_name?: string;
  inspection_type?: string;
  action?: string;
  [key: string]: unknown;
}

/** Car.info levererar ofta datum som YYYYMMDD i extended_attributes (t.ex. e1159). */
export function formatCarInfoYyyymmdd(raw: unknown): string | null {
  if (raw === null || raw === undefined) return null;
  const s = String(raw).replace(/\D/g, '');
  if (s.length < 8) return null;
  const y = s.slice(0, 4);
  const m = s.slice(4, 6);
  const d = s.slice(6, 8);
  const dt = new Date(`${y}-${m}-${d}`);
  if (Number.isNaN(dt.getTime())) return null;
  return dt.toLocaleDateString('sv-SE');
}

export function displayAttrValues(values: unknown[] | undefined, unit?: string): string {
  if (!values || values.length === 0) return '';
  const joined = values.map((v) => (v === null || v === undefined ? '' : String(v))).filter(Boolean).join(', ');
  if (!joined) return '';
  if (unit && unit !== 'n/a' && unit.trim()) return `${joined} ${unit}`.trim();
  return joined;
}

export function getExtendedById(attrs: CarInfoExtendedAttr[] | undefined, id: string): CarInfoExtendedAttr | undefined {
  if (!attrs?.length) return undefined;
  return attrs.find((a) => a.id === id);
}

/** Nästa besiktning / i trafik — importeras från carInfoSpecIds (re-export ovan). */

export function findSpecAttrById(
  specsFlat: CarInfoExtendedAttr[],
  id: string
): CarInfoExtendedAttr | undefined {
  return specsFlat.find((a) => String(a.id ?? '') === id);
}

/** Numeriskt belopp från spec → "3 744 kr" (Car.info anger ofta heltal SEK). */
export function formatCarInfoSekAmount(values: unknown[] | undefined): string {
  if (!values?.length) return '';
  const raw = values[0];
  const n = typeof raw === 'number' ? raw : Number(String(raw).replace(/\s/g, '').replace(',', '.'));
  if (!Number.isFinite(n)) return displayAttrValues(values, undefined);
  return `${Math.round(n).toLocaleString('sv-SE')} kr`;
}

export function latestInspectionFromHistory(history: CarInfoHistoryEvent[] | undefined): CarInfoHistoryEvent | null {
  if (!history?.length) return null;
  const inspections = history.filter((e) => (e.type || '').toLowerCase() === 'inspection');
  if (inspections.length === 0) return null;
  inspections.sort((a, b) => {
    const ta = a.date ? new Date(a.date).getTime() : 0;
    const tb = b.date ? new Date(b.date).getTime() : 0;
    return tb - ta;
  });
  return inspections[0] ?? null;
}

export function flattenCarInfoSpecAttrs(specifications: unknown[] | undefined): CarInfoExtendedAttr[] {
  if (!specifications?.length) return [];
  const out: CarInfoExtendedAttr[] = [];
  for (const cat of specifications as { attributes?: Record<string, CarInfoExtendedAttr> }[]) {
    const attrs = cat?.attributes;
    if (!attrs || typeof attrs !== 'object') continue;
    for (const val of Object.values(attrs)) {
      const v = val as CarInfoExtendedAttr;
      if (v?.id != null) {
        out.push({
          id: v.id,
          name: v.name,
          unit: v.unit,
          values: v.values,
          attributes: v.attributes,
        });
      }
    }
  }
  return out;
}
